import { EntityId } from "@reduxjs/toolkit";
import { streamingApi } from "./base.api";
import { Port } from "types/Port";
import { delay } from "utils/delay";
import { MediaClipType } from "types/MediaClipType";

const BASE_PATH = "/site-commands";

export const siteCommandsApi = streamingApi.injectEndpoints({
    endpoints: (build) => ({
        // playMediaClip
        playMediaClip: build.mutation<
            boolean,
            { siteId: EntityId; mediaClipType: MediaClipType }
        >({
            query: (arg) => ({
                url: `${BASE_PATH}/Sites/${arg.siteId}/MediaClips/Type/${arg.mediaClipType}/Play`,
                method: "PUT",
            }),
        }),

        // site mute
        getAudioMuted: build.query<boolean, EntityId>({
            query: (siteId: EntityId) =>
                `${BASE_PATH}/Sites/${siteId}/audio/muted`,
            providesTags: (result, error, arg) => [
                { type: "SiteMute", id: arg },
            ],
            transformResponse: (data: string) => {
                return data.toLocaleLowerCase() === "muted";
            },
        }),
        updateAudioMuted: build.mutation<
            boolean,
            {
                siteId: EntityId;
                isMuted: boolean;
            }
        >({
            query: (arg) => ({
                url: `${BASE_PATH}/Sites/${arg.siteId}/audio/muted`,
                method: "PUT",
                body: JSON.stringify(arg.isMuted),
            }),
            invalidatesTags: (result, error, arg) => [
                { type: "SiteMute", id: arg.siteId },
            ],
        }),

        // ports (doors, etc)
        getAvailablePorts: build.query<Port[], EntityId>({
            query: (siteId: EntityId) => `${BASE_PATH}/Sites/${siteId}/ports`,
        }),
        getPortActiveState: build.query<
            boolean,
            { siteId: EntityId; portName: string }
        >({
            query: (arg) =>
                `${BASE_PATH}/Sites/${arg.siteId}/ports/${arg.portName}/state`,
            providesTags: (result, error, arg) => [
                { type: "PortState", id: `${arg.siteId}_${arg.portName}` },
            ],
        }),
        updatePortActiveState: build.mutation<
            boolean,
            {
                siteId: EntityId;
                portName: string;
                activate: boolean;
            }
        >({
            query: (arg) => ({
                url: `${BASE_PATH}/Sites/${arg.siteId}/ports/${arg.portName}/state`,
                method: "PUT",
                body: JSON.stringify(arg.activate),
            }),
            invalidatesTags: (result, error, arg) => [
                { type: "PortState", id: `${arg.siteId}_${arg.portName}` },
            ],
            async onQueryStarted({ siteId, portName }, { dispatch }) {
                // Introduce a small delay to give the actual port update a head start before introducing a refetch of the port state by invalidating the related tag
                await delay(250);

                dispatch(
                    siteCommandsApi.util.invalidateTags([
                        { type: "PortState", id: `${siteId}_${portName}` },
                    ]),
                );
            },
        }),
    }),
    overrideExisting: true,
});

export const {
    usePlayMediaClipMutation,
    useGetAudioMutedQuery,
    useUpdateAudioMutedMutation,
    useGetAvailablePortsQuery,
    useGetPortActiveStateQuery,
    useUpdatePortActiveStateMutation,
} = siteCommandsApi;
